import { GlobalStore } from '@roc/feature-app-core';
import { isPublicLinkError } from '@roc-capital/public-link';
import { makeObservable, override, action } from 'mobx';
import { PrivatePlaidService } from '../../../../apps/client-portal-public/src/app/services/privatePlaidService';
import { PublicLinkAchService, PublicLinkRequest } from '../services/publicLinkAchService';
import { AchFormBaseStore } from './achFormStore';

const SUMMARY_STEP = 2;

const noSession: PublicLinkRequest = () =>
  Promise.reject(new Error('PublicLinkAchFormStore: call setRequest() with the session request first'));

/**
 * The ACH form store for the public link flow (/link/ach). Same screens and steps as AchFormStore; the calls go to
 * vendor-service's session-guarded endpoints (PublicLinkAchService) instead of the signed-in ones.
 *
 * Created with the app's root store, before there is a session: the page passes the session's request in with
 * setRequest() once usePublicLinkSession has one. Until then every call rejects.
 */
export class PublicLinkAchFormStore extends AchFormBaseStore {
  private achService: PublicLinkAchService;

  constructor(globalStore: GlobalStore) {
    super(globalStore);
    this.setRequest(noSession);
    makeObservable(this, {
      setRequest: action,
      getACHData: override,
      generateDocument: override,
      disableStartACHForm: override,
    });
  }

  /** The authorized fetch from usePublicLinkSession; every call goes through it. */
  setRequest(request: PublicLinkRequest) {
    this.achService = new PublicLinkAchService(request);
    // The base store's bank step calls sentBofaValidation and saveBankInformation, which PublicLinkAchService provides.
    this.plaidService = (this.achService as unknown) as PrivatePlaidService;
  }

  *getACHData(_ignored?: string) {
    return yield this.achService.getACHData();
  }

  /** A second submit (409) means the form is already signed: show the summary rather than an error. */
  *generateDocument(signature): Generator<unknown, void, unknown> {
    try {
      yield this.achService.generateAchDocument({ signature, userAgent: navigator.userAgent });
      this.activeStep = SUMMARY_STEP;
    } catch (error) {
      if (isPublicLinkError(error) && error.kind === 'COMPLETED') {
        this.activeStep = SUMMARY_STEP;
        return;
      }
      if (isPublicLinkError(error) && error.kind === 'INVALID') {
        // The page switches to "session ended"; no toast needed.
        return;
      }
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while generating the ACH document. Please try again.',
      });
    }
  }

  /** The link is for one borrower, so there is no borrower id to wait for. */
  get disableStartACHForm() {
    return !this.achData;
  }
}
